/**
 * In-memory auth store. The host delivers the Bearer token + tenant id via the
 * postMessage handshake; we deliberately keep them in memory only (never
 * localStorage / sessionStorage / URL) so an embedded token can't be exfiltrated
 * from persistent storage and is dropped the moment the iframe unloads.
 */

import { getJwtExpiryMs } from './jwt';

let authToken = '';
let tenantId = '';

export function setAuth(token: string, tenant: string): void {
  authToken = token;
  tenantId = tenant;
}

export function setToken(token: string): void {
  authToken = token;
}

export function clearAuth(): void {
  authToken = '';
  tenantId = '';
}

export function getAuthToken(): string {
  // Dev-only fallback so the app is usable without a host during local dev.
  // Gated on Vite's built-in DEV flag (not VITE_ENV) so this branch — and the
  // VITE_DEV_TOKEN reference — is dead-code-eliminated from every `vite build`
  // output, matching how DevModePanel is stripped (see App.tsx).
  if (!authToken && import.meta.env.DEV) {
    return import.meta.env.VITE_DEV_TOKEN?.trim() || '';
  }
  return authToken;
}

export function getTenantId(): string {
  if (!tenantId && import.meta.env.DEV) {
    return import.meta.env.VITE_DEV_TENANT_ID?.trim() || '';
  }
  return tenantId;
}

export function hasAuth(): boolean {
  return Boolean(getAuthToken() && getTenantId());
}

/** Expiry of the current access token (epoch ms), or null if unknown. */
export function getAuthTokenExpiry(): number | null {
  const token = getAuthToken();
  return token ? getJwtExpiryMs(token) : null;
}
